import { MutationCacheNotifyEvent, QueryClient, QueryKey } from "@tanstack/react-query";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import uniqWith from "lodash/uniqWith";
import { ONE_DAY_MS } from "PFCore/utilities/time_consts";

import { InvalidationMap, queryInvalidationMap } from "./query_invalidation_map";

export interface MutationMeta extends Record<string, unknown> {
  invalidation?: {
    disabled?: boolean;
    replace?: boolean;
    queryKeys?: QueryKey[];
    composeQueryKeys?: (response: any, payload: any) => QueryKey[];
  };
}

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: MutationMeta;
  }
}

/**
 * @description
 * A global onSuccess handler for all mutations. It looks up the mutation's resource
 * in our `invalidationMap` and invalidates all associated query resources.
 */
export const applyMutationInvalidation = async (
  event: MutationCacheNotifyEvent,
  client: QueryClient,
  invalidationMap: InvalidationMap
) => {
  if (event.type !== "updated" || event.mutation.state.status !== "success") {
    return;
  }

  const { data, variables } = event.mutation.state;
  const { mutationKey, meta } = event.mutation.options as { mutationKey: QueryKey; meta: MutationMeta };

  const {
    disabled = false,
    replace: replaceDefault = false,
    queryKeys = [],
    composeQueryKeys
  } = meta?.invalidation || {};

  if (
    disabled ||
    isEmpty(mutationKey) ||
    (!((mutationKey[0] as string) in invalidationMap) && !replaceDefault)
  ) {
    return;
  }

  // Get the keys to invalidate
  // For `replace` flag we use only the custom keys
  // Otherwise we use the invalidation map keys + custom keys
  const resource = mutationKey[0] as string;

  const generatedKeys = composeQueryKeys ? composeQueryKeys(data, variables) : [];
  const customKeys = [...queryKeys, ...generatedKeys];
  const sourceKeys = replaceDefault ? customKeys : [...(invalidationMap[resource] || []), ...customKeys];

  // Deduplicate the keys
  const resourceKeysToInvalidate = uniqWith(sourceKeys, isEqual);

  if (resourceKeysToInvalidate && resourceKeysToInvalidate.length > 0) {
    const invalidationPromises = resourceKeysToInvalidate.map((key) =>
      client.invalidateQueries({ queryKey: key })
    );

    await Promise.all(invalidationPromises);
  }
};

function createQueryClient() {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: ONE_DAY_MS,
        refetchOnWindowFocus: false,
        staleTime: Infinity
      }
    }
  });

  client
    .getMutationCache()
    .subscribe((event) => applyMutationInvalidation(event, client, queryInvalidationMap));

  return client;
}

export const queryClient = createQueryClient();
